import React, { memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { uuid4 } from '@sentry/utils';
import { logError } from '../../global/utils/logger';
import { useAnalyticsContext } from '../../../config/GoogleTagManagerEvents';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../constants/analyticsConstants/Ecommerce';
import AlertSuccessCircle from '../../../resources/images/checkmark-circle-30.svg';
import AlertTriangle from '../../../resources/images/alert-triangle-red.svg';
import { useGuidedRegContext } from '../context/guidedRegContext';
import { DIGIT_REGEX } from '../../global/utils/commonUtils';
import { LoadingIndicator } from '../../../aem-core-components';
import { getSubmissionId, verifyToken, getClientIp } from '../api/apiRequest';
import { Button } from '../../../aem-core-components';
import Input from '../../global/atoms/input';
import VerificationFailedModal from '../verificationFailedModal';
import VerificationTimoutModal from '../VerificationTimoutModal';
import { STORAGE_CONFIG } from '../../../constants/storageConfig';
import {
    getCreateAccountObject,
    getEmailVerificationObject,
    logGRError,
    navigateToPath,
    redirectToCashFlow,
    refreshTokenCall,
    resendVerifyCode
} from '../utils/guidedUtil';
import {
    API_STATUS,
    ERROR_CODES,
    VERIFY_CODE,
    SUCCESS_CODES,
    PERSONAL_ACCOUNT,
    PAYMENT_PREFERENCE_PATH,
    TWILIO_CODE_LIMIT,
    EMAIL,
    CASH_FLOW_PATH,
    CCA_PATH,
    POD_PATH
} from '../constants';
import { CHANNEL } from '../cashFlow/constants';
import './emailVerify.scss';
import { prefetchResources } from '../../global/utils/prefetch';

const EmailVerify = () => {
    const navigate = useNavigate();
    const intl = useIntl();
    const emailVerificationConfigs = getEmailVerificationObject();
    const createAccountConfigs = getCreateAccountObject();
    const userExists = sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.USER_EXISTS);
    const {
        resendCodeButtonLabel = '',
        emailVerificationTitleLabel = '',
        verificationCodeLabel = '',
        emailVerificationDescription = '',
        resendCodeDisclaimer = '',
        resendCodeErrorMessage = ''
    } = emailVerificationConfigs || {};
    const {
        sendEventsForEmailVerifyFormStarted,
        sendEventsForEmailVerifyFormSubmitted,
        sendEventsForEmailVerifyFormError,
        sendEventsForEmailVerifyFormCompleted,
        sendEventsForModalClosed,
        sendEventsForPageViewed
    } = useAnalyticsContext();
    const [{ emailId, accountType }] = useGuidedRegContext();
    const { continueButtonLabel = '', requiredFieldsLabel = '', disablePodFlow = 'false' } = createAccountConfigs || {};
    const [formStarted, setFormStarted] = useState(false);
    const [failedModal, setFailedModal] = useState(false);
    const [timeoutModal, setTimeoutModal] = useState(false);
    const [showOtpSendBanner, setShowOptSendBanner] = useState(false);
    const [showTooManyRequestsBanner, setShowTooManyRequestsBanner] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [verifyCode, setVerifyCode] = useState('');
    const [errorCode, setErrorCode] = useState();
    const enablePOD = disablePodFlow === 'false';

    const emailVerifyErrorMsg = {
        verifyCode: intl.formatMessage({ id: 'verify_code' }),
        invalidCode: intl.formatMessage({ id: 'invalid_code' })
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        sendEventsForPageViewed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PAGE_VIEWED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_NAME,
            window.location.href
        );

        if (PERSONAL_ACCOUNT === accountType) {
            prefetchResources(CASH_FLOW_PATH);
        } else {
            if (!enablePOD) {
                return prefetchResources(CCA_PATH);
            }
            prefetchResources([CCA_PATH, POD_PATH]);
        }
    }, []);

    const handleInputChange = event => {
        setVerifyCode(event.target.value.replace(DIGIT_REGEX, ''));
    };

    const handleSubmit = event => {
        event.preventDefault();
    };

    const resendOtp = async () => {
        try {
            setShowTooManyRequestsBanner(false);
            setShowOptSendBanner(false);
            setIsLoading(true);
            const response = await resendVerifyCode(emailId, EMAIL);
            if (response.status === SUCCESS_CODES.SUCCESS_CODE_201) {
                setShowOptSendBanner(true);
            } else if (response.error) {
                const {
                    error: {
                        response: { status }
                    }
                } = response;
                if (status === ERROR_CODES.TOO_MANY_REQUESTS) {
                    setShowTooManyRequestsBanner(true);
                } else {
                    throw response.error;
                }
            }
            setIsLoading(false);
        } catch (error) {
            logGRError(error);
            setIsLoading(false);
        }
    };

    const onContinue = async () => {
        setIsLoading(true);
        sendEventsForEmailVerifyFormSubmitted(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_SUBMITTED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_NAME,
            accountType
        );
        if (!verifyCode) {
            sendEventsForEmailVerifyFormError(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_NAME,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR_EMPTY_FIELD,
                accountType
            );
            setErrorCode(emailVerifyErrorMsg.verifyCode);
            setIsLoading(false);
        }
        // otp having less than 6 digits
        else if (verifyCode.length < TWILIO_CODE_LIMIT) {
            sendEventsForEmailVerifyFormError(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_NAME,
                emailVerifyErrorMsg.invalidCode,
                accountType
            );
            setErrorCode(emailVerifyErrorMsg.invalidCode);
            setIsLoading(false);
        } else {
            try {
                await validateVerificationCode(); // Wait for the verification process to complete
            } catch (error) {
                logError(error);
                setIsLoading(false);
            }
        }
    };

    const redirectToPath = () => {
        sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.GR_EMAIL, emailId);
        if (accountType === PERSONAL_ACCOUNT) {
            redirectToCashFlow();
        } else {
            if (!enablePOD) {
                return window.location.replace(CCA_PATH);
            }
            navigateToPath(navigate, PAYMENT_PREFERENCE_PATH);
        }
    };

    const generateSubmissionId = async () => {
        try {
            const responseclientIP = await getClientIp();
            if (responseclientIP.status === SUCCESS_CODES.SUCCESS_CODE_200) {
                const clientIP = responseclientIP.data.ip;
                const sessionID =  uuid4().replace(/-/g, '');
                sessionStorage?.setItem(STORAGE_CONFIG.SESSION_STORAGE.CLIENT_IP_ADDRESS, clientIP);
                sessionStorage?.setItem(STORAGE_CONFIG.SESSION_STORAGE.SESSIONID, sessionID);

                const payload = {
                    email: emailId,
                    IsCredit: accountType === PERSONAL_ACCOUNT ? false : true,
                    sessionID: sessionID,
                    clientIPAddress: clientIP,
                    referrer: window.location.href,
                    sourceApplication: CHANNEL,
                    isExistingProfile: userExists == 'true',
                    sbsid: sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.SBSID) || '',
                    userType: accountType === PERSONAL_ACCOUNT ? 'noncredit' : 'credit',
                    otp: verifyCode,
                };
                const response = await getSubmissionId(payload);
                const {
                    data: { data: { status: status = '' } = {} } = {},
                    error: { response: { status: errorStatus = '' } = {} } = {},
                    status: apiStatus,
                } = response || {};

                if (status === API_STATUS.PENDING) {
                    setErrorCode(emailVerifyErrorMsg.invalidCode);
                    sendEventsForEmailVerifyFormError(
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR,
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_NAME,
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR_INCORRECT_OTP,
                        accountType
                    );
                } else if (apiStatus === SUCCESS_CODES.SUCCESS_CODE_200) {
                    // send analytics event for success
                    sendEventsForEmailVerifyFormCompleted(
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_COMPLETED,
                        EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_NAME,
                        accountType
                    );

                    const { data: { data: { SubmissionID: submissionId = {} } = {} } = {} } = response;
                    sessionStorage?.setItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_SUBMISSION_ID, submissionId);
                    sessionStorage?.setItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_VERIFICATION_CODE, verifyCode);
                    redirectToPath();
                } else {
                    if (errorStatus === ERROR_CODES.NOT_FOUND) {
                        sendEventsForEmailVerifyFormError(
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_NAME,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR_TIMEOUT,
                            accountType
                        );
                        //verification timeout modal
                        setTimeoutModal(true);
                    } else if (errorStatus === ERROR_CODES.TOO_MANY_REQUESTS) {
                        sendEventsForEmailVerifyFormError(
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_NAME,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR_TOO_MANY_REQUESTS,
                            accountType
                        );
                        //verification failed modal
                        setFailedModal(true);
                    } else {
                        setErrorCode(emailVerifyErrorMsg.invalidCode);
                        sendEventsForEmailVerifyFormError(
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_NAME,
                            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_ERROR_INCORRECT_OTP,
                            accountType
                        );
                        throw response.error;
                    }
                }

                setIsLoading(false);
            } else {
                setIsLoading(false);
                throw responseclientIP;
            }
        } catch (error) {
            logGRError(error);
            setIsLoading(false);
        }
    };

    const validateVerificationCode = async () => {
        try {
            await refreshTokenCall();
            generateSubmissionId();
        } catch (error) {
            logGRError(error);
            setIsLoading(false);
        }
    };

    const hideFailedModal = () => {
        setFailedModal(false);
        sendEventsForModalClosed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CTA_CLICKED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_CLOSE,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_BUTTON,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_MODAL,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CLOSE
        );
    };

    const hideTimeoutModal = () => {
        setTimeoutModal(false);
        sendEventsForModalClosed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CTA_CLICKED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_CLOSE,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_BUTTON,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_MODAL,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CLOSE
        );
    };

    const onInputFieldFocus = () => {
        if (!formStarted) {
            sendEventsForEmailVerifyFormStarted(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_STARTED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_EMAIL_VERIFY_FORM_NAME,
                accountType
            );
            setFormStarted(true);
        }
        setShowOptSendBanner(false);
        setShowTooManyRequestsBanner(false);
        setErrorCode('');
    };

    const renderResendOtpBanner = () => {
        return (
            <div className="verify-mail__resend-otp-view" data-testid="resendOtpBanner">
                <AlertSuccessCircle />
                <span className="verify-mail__resend-otp-view-text">{resendCodeDisclaimer}</span>
            </div>
        );
    };

    const renderTooManyRequestsBanner = () => {
        return (
            <div className="verify-mail__toManyRequests" data-testid="tooManyResendOTPRequest">
                <div className="verify-mail__toManyRequests-icon">
                    <AlertTriangle />
                </div>
                <span className="verify-mail__toManyRequests-text">{resendCodeErrorMessage}</span>
            </div>
        );
    };

    return (
        <div className={isLoading ? 'loading_overlay_background' : ''}>
            <section className="verify-mail__section">
                <div className="container">
                    <div className="row verify-mail__row">
                        <div className="col-lg-6">
                            <div className="form-heading verify-mail__heading">
                                <h2 className="verify-mail__title">{emailVerificationTitleLabel}</h2>
                                <small className="verify-mail__require-field-text">{requiredFieldsLabel}</small>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <fieldset className="verify-mail__fieldset">
                                    <legend className="verify-mail__legend">{emailVerificationDescription}</legend>
                                    <div className="verify-mail__content">
                                        <Input
                                            name={VERIFY_CODE}
                                            handleInputChange={handleInputChange}
                                            label={verificationCodeLabel}
                                            inputAriaRequired={true}
                                            required={true}
                                            maxLength={6}
                                            dataTestId="verify_token"
                                            handleInputFocus={onInputFieldFocus}
                                            value={verifyCode}
                                            errorMsg={errorCode}
                                            showErrorIcon={true}
                                        />

                                        <Button
                                            className="button button-block verify-mail__send_codes"
                                            type="button"
                                            onClick={resendOtp}
                                            buttonAriaLabel={resendCodeButtonLabel}>
                                            {resendCodeButtonLabel}
                                        </Button>
                                    </div>
                                </fieldset>
                                {showOtpSendBanner ? renderResendOtpBanner() : null}
                                {showTooManyRequestsBanner ? renderTooManyRequestsBanner() : null}
                                <Button
                                    className="button button-primary button-block"
                                    type="button"
                                    onClick={onContinue}
                                    buttonAriaLabel="Continue">
                                    {continueButtonLabel}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>

                {failedModal && (
                    <VerificationFailedModal
                        failedModal={failedModal}
                        hideFailedModal={hideFailedModal}
                        resendCode={resendOtp}
                    />
                )}
                {timeoutModal && (
                    <VerificationTimoutModal
                        timeoutModal={timeoutModal}
                        hideTimeoutModal={hideTimeoutModal}
                        resendCode={resendOtp}
                    />
                )}
            </section>
            {isLoading && (
                <div className="globalmodal__overlay loading_overlay">
                    <LoadingIndicator />
                </div>
            )}
        </div>
    );
};

export default memo(EmailVerify);
